import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, ShieldCheckIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
export function HeroSection() {
  const handleCTAClick = () => {
    const ctaButton = document.getElementById('cta-button');
    if (ctaButton) {
      ctaButton.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  return <section className="relative w-full min-h-screen flex items-center justify-center px-4 py-16 md:py-24 overflow-hidden" data-id="element-94">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy-lighter opacity-90" data-id="element-95" />

      {/* Floating gold orbs for visual interest */}
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-20 left-10 w-64 h-64 bg-gold/20 rounded-full blur-3xl" data-id="element-96" />
      <motion.div animate={{
      scale: [1, 1.3, 1],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-20 right-10 w-96 h-96 bg-gold-light/20 rounded-full blur-3xl" data-id="element-97" />

      <div className="relative z-10 max-w-7xl mx-auto" data-id="element-98">
        {/* Trust badges */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex flex-wrap justify-center gap-4 mb-8" data-id="element-99">
          <div className="flex items-center gap-2 bg-navy-lighter/80 backdrop-blur-sm border border-gold/30 rounded-full px-4 py-2" data-id="element-100">
            <TrendingUpIcon className="w-5 h-5 text-gold" data-id="element-101" />
            <span className="text-sm text-gold-light font-semibold" data-id="element-102">
              Diseñado para latinos en USA
            </span>
          </div>
          <div className="flex items-center gap-2 bg-navy-lighter/80 backdrop-blur-sm border border-gold/30 rounded-full px-4 py-2" data-id="element-103">
            <ShieldCheckIcon className="w-5 h-5 text-gold" data-id="element-104" />
            <span className="text-sm text-gold-light font-semibold" data-id="element-105">
              Método 100% legal
            </span>
          </div>
          <div className="flex items-center gap-2 bg-navy-lighter/80 backdrop-blur-sm border border-gold/30 rounded-full px-4 py-2" data-id="element-106">
            <ClockIcon className="w-5 h-5 text-gold" data-id="element-107" />
            <span className="text-sm text-gold-light font-semibold" data-id="element-108">
              Paso a paso
            </span>
          </div>
        </motion.div>

        {/* Hero Image - Now in first row for maximum visibility */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        y: -30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: 'easeOut'
      }} className="flex justify-center mb-12" data-id="element-109">
          <div className="relative max-w-lg w-full" data-id="element-110">
            {/* Enhanced glow effect - larger and more prominent */}
            <motion.div animate={{
            scale: [1, 1.15, 1],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -inset-8 bg-gradient-to-r from-gold via-gold-light to-gold blur-3xl" data-id="element-111" />

            {/* Additional spotlight effect */}
            <motion.div animate={{
            opacity: [0.3, 0.6, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -inset-4 bg-gold-light/40 blur-2xl" data-id="element-112" />

            {/* Image with enhanced border and shadow */}
            <div className="relative z-10 border-[6px] border-gold rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.6)]" data-id="element-113">
              <motion.img animate={{
              boxShadow: ['0 0 40px rgba(212,175,55,0.4)', '0 0 60px rgba(212,175,55,0.6)', '0 0 40px rgba(212,175,55,0.4)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} src="https://cdn.magicpatterns.com/uploads/3mtEumAzSK4rhNuai8yGzd/imagen_la_la_portada_de_mi_pagina.png" alt="Mejora tu Credit Score - Sistema de Optimización Crediticia en 90 Días" className="w-full h-auto" data-id="element-114" />
            </div>

            {/* Floating badge - enhanced */}
            <motion.div initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 1
          }} whileHover={{
            scale: 1.1
          }} className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gold-light to-gold-dark text-navy px-8 py-5 rounded-2xl shadow-[0_0_30px_rgba(212,175,55,0.8)] border-4 border-navy" data-id="element-115">
              <div className="text-center" data-id="element-116">
                <div className="text-4xl font-heading font-black" data-id="element-117">90</div>
                <div className="text-xs font-bold uppercase tracking-wider" data-id="element-118">
                  Días
                </div>
              </div>
            </motion.div>

            {/* Decorative corner accents */}
            <div className="absolute -top-3 -left-3 w-8 h-8 border-t-4 border-l-4 border-gold-light rounded-tl-lg" data-id="element-119" />
            <div className="absolute -top-3 -right-3 w-8 h-8 border-t-4 border-r-4 border-gold-light rounded-tr-lg" data-id="element-120" />
            <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-4 border-l-4 border-gold-light rounded-bl-lg" data-id="element-121" />
            <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-4 border-r-4 border-gold-light rounded-br-lg" data-id="element-122" />
          </div>
        </motion.div>

        {/* Text Content - Now below the image */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5,
        ease: 'easeOut'
      }} className="text-center max-w-4xl mx-auto" data-id="element-123">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="text-5xl md:text-6xl lg:text-7xl font-heading font-black leading-tight mb-6" data-id="element-124">
            <span className="text-gold-metallic block mb-2 drop-shadow-[0_0_30px_rgba(212,175,55,0.5)]" data-id="element-125">
              ¿Cansado de que te rechacen por tu crédito?
            </span>
            <span className="text-white block text-3xl md:text-4xl lg:text-5xl" data-id="element-126">
              Aprende exactamente qué afecta tu{' '}
              <span className="text-gold-metallic" data-id="element-127">credit score</span> y cómo
              mejorarlo en <span className="text-gold-metallic" data-id="element-128">90 días</span>
            </span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="mb-8 max-w-2xl mx-auto" data-id="element-129">
            <div className="space-y-3 mb-6" data-id="element-130">
              <div className="flex items-center gap-3" data-id="element-131">
                <CheckCircleIcon className="w-6 h-6 text-yellow-400 flex-shrink-0" data-id="element-132" />
                <span className="text-lg md:text-xl text-white font-semibold" data-id="element-133">
                  Recupera el control de tu crédito
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-134">
                <CheckCircleIcon className="w-6 h-6 text-yellow-400 flex-shrink-0" data-id="element-135" />
                <span className="text-lg md:text-xl text-white font-semibold" data-id="element-136">
                  Menos de lo que cuesta un café
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-137">
                <CheckCircleIcon className="w-6 h-6 text-yellow-400 flex-shrink-0" data-id="element-138" />
                <span className="text-lg md:text-xl text-yellow-400 font-semibold" data-id="element-139">
                  Accede ahora mismo por tan solo
                </span>
              </div>
            </div>
            <div className="text-center" data-id="element-140">
              <span className="block text-5xl md:text-6xl font-heading font-black text-gold-metallic" data-id="element-141">
                $6.36
              </span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="flex flex-col sm:flex-row gap-4 justify-center mb-6" data-id="element-142">
            <motion.button onClick={handleCTAClick} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-10 py-5 bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy text-xl md:text-2xl font-heading font-black rounded-xl shadow-2xl btn-pulse hover:shadow-gold/50 transition-all duration-300" data-id="element-143">
              👉 QUIERO MEJORAR MI CRÉDITO
            </motion.button>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 1.2
        }} className="text-sm text-gray-400" data-id="element-144">
            ✓ Acceso inmediato • ✓ Sin suscripciones
          </motion.p>
        </motion.div>
      </div>
    </section>;
}